import { Box, Typography, useTheme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemLinearMeter, type SystemLinearMeterScaleMark } from "../../../../components/UIToolkit/DataViz/SystemLinearMeter/SystemLinearMeter";
import { FONT_SIZE } from "../../../../theme/tokens";
import type { PbjPrediction } from "../../../../utils/models";
import { goldAccent, heroAccentInk, heroInk, heroInkMuted, heroProgressFill, heroProjectedHatch, heroTrack } from "../colors";

type Props = {
	prediction: PbjPrediction;
	// True when an Action Plan lifts the score beyond current — draws the hatched projection.
	hasActivePlan: boolean;
	// Current PBJ points, rounded — the "now" end of the meter label.
	currentPointsRounded: number;
	// Plan-projected points, rounded — the "with-plan" end of the meter label.
	withPlanPoints: number;
	// The rounded target points — the gold tick + label.
	roundedTarget: number;
	// The plan's projected points (unrounded) — the meter's projected extension fill.
	projectedPoints: number;
	// Raw available-measure points — the numerator of the CMS rescale note (only when rescaled).
	availablePoints: number;
};

/**
 * The hero's now → projected → target progress meter, pulled out of the gap story so it can
 * run FULL-WIDTH across the bottom of the hero band (a real horizontal anchor instead of a
 * thin bar confined to the narrow middle column). Solid brand-ramp fill for "now", a hatched
 * extension for the with-plan projection, and a gold target tick. Pure presentation.
 */
export const HeroProgressMeter: FC<Props> = ({
	prediction,
	hasActivePlan,
	currentPointsRounded,
	withPlanPoints,
	roundedTarget,
	projectedPoints,
	availablePoints,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const gold = goldAccent(theme);

	const scaleMarks = useMemo<SystemLinearMeterScaleMark[]>(() => {
		const marks: SystemLinearMeterScaleMark[] = [
			{ value: prediction.totalPoints, label: String(currentPointsRounded), color: heroInk() },
			{ value: roundedTarget, label: String(roundedTarget), color: gold },
			{ value: prediction.maxPoints, label: String(Math.round(prediction.maxPoints)), color: heroInkMuted() },
			{ value: 0, label: "0", color: heroInkMuted() },
		];
		if (hasActivePlan) marks.push({ value: projectedPoints, label: String(withPlanPoints), color: heroAccentInk(theme) });
		return marks;
	}, [prediction.totalPoints, prediction.maxPoints, currentPointsRounded, roundedTarget, hasActivePlan, projectedPoints, withPlanPoints, gold, theme]);

	return (
		<Box sx={{ width: "100%" }}>
			{/* No legend row above the bar: the axis labels each sit ON their mark and are tinted
			    like it (white now / accent projection / gold target), so a "Now X · with plan Y …
			    target Z" line would only restate the axis — and the score lead already names the
			    now + with-plan numbers, the verdict the target. The full sentence stays in the
			    meter's `ariaLabel` for screen readers. */}
			<SystemLinearMeter
				value={prediction.totalPoints}
				max={prediction.maxPoints}
				color={heroProgressFill(theme)}
				trackColor={heroTrack()}
				secondaryValue={hasActivePlan ? projectedPoints : undefined}
				secondaryFill={heroProjectedHatch(theme)}
				targetRatio={prediction.maxPoints > 0 ? roundedTarget / prediction.maxPoints : 0}
				targetColor={gold}
				scaleMarks={scaleMarks}
				scaleLabelColor={heroInkMuted()}
				height={12}
				ariaLabel={
					hasActivePlan
						? t("pbj_model.stars.progress_aria_with_plan", { now: currentPointsRounded, plan: withPlanPoints, target: roundedTarget })
						: t("pbj_model.stars.progress_aria", { now: currentPointsRounded, target: roundedTarget })
				}
				dataTestId="pbj-model-hero-progress"
			/>
			{prediction.rescaled && (
				<Typography sx={{ display: "block", mt: 0.875, fontSize: FONT_SIZE.LABEL_SM, color: heroInkMuted() }} data-testid="pbj-model-rescaled-note">
					{t("pbj_model.stars.rescaled_note", { raw: availablePoints.toFixed(1), availableMax: prediction.availableMaxPoints, max: prediction.maxPoints })}
				</Typography>
			)}
		</Box>
	);
};
